import { ContentApiClient } from "@/lib/api/client";
import { ApiNotFoundError } from "@/lib/api/types";
import type {
  CatalogItemDetail,
  CatalogItemSummary,
  LocaleCode,
  ReviewsListEnvelope,
  SeoBlock,
  StoreCategorySeo,
} from "@/lib/api/types";
import { getCurrentLocale } from "@/lib/utils/locale";

/**
 * Shared Odoo store-catalog access, used both by the /api/store/* BFF routes
 * (for client-side re-fetches) and directly by page loaders on the server
 * (avoiding a self-referential HTTP round-trip during SSR, which is fragile
 * in dev — the dev server's actual port can differ from VITE_SITE_URL — and
 * unnecessary indirection in production).
 */
function getApiClient(sessionId?: string) {
  const baseUrl = process.env.VITE_ODOO_BASE_URL || "http://localhost:8069";
  return new ContentApiClient({ baseUrl, sessionId });
}

/**
 * `sessionId` makes Odoo price the response through the buyer's own pricelist
 * instead of list price.
 *
 * Deliberately never passed on the SSR path. The server-rendered HTML is the
 * public, cacheable, crawlable page, and baking one customer's negotiated
 * price into it is how a cache serves that price to the next visitor. Partner
 * pricing arrives after hydration, through the BFF route below, which reads
 * the session cookie off the browser's own request.
 */
export async function getStoreProducts(
  locale: LocaleCode,
  sessionId?: string,
): Promise<{
  data: CatalogItemSummary[];
  placeholder: boolean;
  /** meta.categories — the per-locale category URLs the category page's hreflang needs. */
  categories?: StoreCategorySeo[];
}> {
  try {
    const response = await getApiClient(sessionId).listStoreProducts(locale);
    return { data: response.data, placeholder: false, categories: response.meta?.categories };
  } catch (error) {
    console.warn("[Store Data] Odoo product list error:", error);
    return { data: [], placeholder: true };
  }
}

export type StoreProductResult =
  | {
      status: "ok";
      data: CatalogItemDetail;
      /**
       * Odoo's own SEO block for this product. Carries `hreflang` — the real
       * per-locale URL for each locale this product exists in — which is the
       * only trustworthy source for the shop's language alternates; __root.tsx
       * would otherwise guess them by locale-prefix substitution, which is
       * wrong whenever the slug differs per locale. May be absent: the field is
       * in the API contract but not every deployment populates it, so callers
       * must degrade to emitting no alternates rather than to guessing.
       */
      seo?: SeoBlock;
    }
  | {
      status: "not_found";
      /**
       * This locale's real path for the id in the requested slug, when the API
       * named one — i.e. the URL was another locale's slug or a misspelling of
       * a live product. The route 301s to it rather than 404ing (VARS-125).
       */
      canonicalPath?: string;
    }
  | { status: "unavailable" };

/**
 * Unlike the curated /products endpoint, the shop detail route
 * (GET /api/v1/store/products/{locale}/{url_path}) resolves the trailing
 * slug straight to a product id via Odoo's own ir.http._unslug() — no
 * list-then-match dance needed, the bare slug from our flat /shop/$slug
 * route works directly.
 */
export async function getStoreProduct(
  locale: LocaleCode,
  slug: string,
  sessionId?: string,
): Promise<StoreProductResult> {
  try {
    const response = await getApiClient(sessionId).getStoreProduct(locale, slug);
    return { status: "ok", data: response.data, seo: response.seo };
  } catch (error) {
    if (error instanceof ApiNotFoundError)
      return { status: "not_found", canonicalPath: error.canonicalPath };
    console.error("[Store Data] Odoo product detail error:", error);
    return { status: "unavailable" };
  }
}

/** Isomorphic: direct Odoo client call on the server, BFF fetch in the browser. */
export async function loadStoreProducts(): Promise<{
  data: CatalogItemSummary[];
  placeholder: boolean;
  categories?: StoreCategorySeo[];
}> {
  const locale = getCurrentLocale();
  if (typeof window === "undefined") {
    return getStoreProducts(locale as LocaleCode);
  }
  const res = await fetch(`/api/store/products?locale=${locale}`);
  // `categories` is forwarded verbatim by the BFF, so a client-side navigation
  // to a category page publishes the same hreflang set the SSR render did.
  // Without that the two disagree, and which one Google sees is a race.
  const body = (await res.json()) as {
    data?: CatalogItemSummary[];
    placeholder?: boolean;
    categories?: StoreCategorySeo[];
  };
  return {
    data: body.data ?? [],
    placeholder: Boolean(body.placeholder),
    categories: body.categories,
  };
}

export async function loadStoreProduct(slug: string): Promise<StoreProductResult> {
  const locale = getCurrentLocale();
  if (typeof window === "undefined") {
    return getStoreProduct(locale as LocaleCode, slug);
  }
  const res = await fetch(`/api/store/products/${encodeURIComponent(slug)}?locale=${locale}`);
  if (res.status === 404) {
    // Forwarded by the BFF so a client-side navigation corrects the slug the
    // same way the server render does.
    const body = (await res.json().catch(() => null)) as { canonical_url_path?: string } | null;
    return { status: "not_found", canonicalPath: body?.canonical_url_path };
  }
  if (!res.ok) return { status: "unavailable" };
  const body = (await res.json()) as { data: CatalogItemDetail; seo?: SeoBlock };
  return { status: "ok", data: body.data, seo: body.seo };
}

export async function getProductReviews(
  locale: LocaleCode,
  slug: string,
): Promise<ReviewsListEnvelope | null> {
  try {
    return await getApiClient().listProductReviews(locale, slug);
  } catch (error) {
    console.warn("[Store Data] Odoo reviews list error:", error);
    return null;
  }
}

export async function loadProductReviews(slug: string): Promise<ReviewsListEnvelope | null> {
  const locale = getCurrentLocale();
  if (typeof window === "undefined") {
    return getProductReviews(locale as LocaleCode, slug);
  }
  const res = await fetch(
    `/api/store/products/${encodeURIComponent(slug)}/reviews?locale=${locale}`,
  );
  if (!res.ok) return null;
  return (await res.json()) as ReviewsListEnvelope;
}
